import React, { createContext, useContext, useEffect, useState, useRef } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState(null);
  const [name, setName] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const hasHydrated = useRef(false);

  // Hydrate from localStorage on first load ONLY
  // Skip if we're in the middle of a login process
  useEffect(() => {
    if (hasHydrated.current || isLoggingIn) {
      return;
    }
    hasHydrated.current = true;

    const stored = localStorage.getItem("auth_token");
    const storedEmail = localStorage.getItem("auth_email");
    const storedName = localStorage.getItem("auth_name");
    const storedAvatar = localStorage.getItem("auth_avatar");

    if (stored) {
      // Validate token is not expired before hydrating
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setToken(stored);
          if (storedEmail) setEmail(storedEmail);
          if (storedName) setName(storedName);
          if (storedAvatar) setAvatarUrl(storedAvatar);
        } else {
          // Token expired, clear storage
          localStorage.removeItem("auth_token");
          localStorage.removeItem("auth_email");
          localStorage.removeItem("auth_name");
          localStorage.removeItem("auth_avatar");
        }
      } catch (e) {
        // Invalid token format, clear storage
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_email");
        localStorage.removeItem("auth_name");
        localStorage.removeItem("auth_avatar");
      }
    }
  }, [isLoggingIn]);

  const login = (jwt, userEmail, remember, displayName, avatar) => {
    // Mark that we're logging in to prevent hydration race
    setIsLoggingIn(true);

    // Clear all cached data FIRST - single authoritative cache clear
    if (window.queryClient) {
      window.queryClient.clear();
      window.queryClient.removeQueries();
      window.queryClient.invalidateQueries();
    }

    // Clear localStorage first to prevent stale data
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_email");
    localStorage.removeItem("auth_name");
    localStorage.removeItem("auth_avatar");

    // Set new auth state SYNCHRONOUSLY - no requestAnimationFrame
    setToken(jwt);
    setEmail(userEmail);
    setName(displayName || null);
    setAvatarUrl(avatar || null);

    // Persist to localStorage if remember is true
    if (remember) {
      localStorage.setItem("auth_token", jwt);
      localStorage.setItem("auth_email", userEmail || "");
      localStorage.setItem("auth_name", displayName || "");
      localStorage.setItem("auth_avatar", avatar || "");
    }

    // Reset logging in flag after state is set
    setIsLoggingIn(false);
  };

  const logout = () => {
    // Clear all cached data on logout
    if (window.queryClient) {
      window.queryClient.clear();
      window.queryClient.removeQueries();
    }

    // Clear state
    setToken(null);
    setEmail(null);
    setName(null);
    setAvatarUrl(null);

    // Clear localStorage
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_email");
    localStorage.removeItem("auth_name");
    localStorage.removeItem("auth_avatar");
  };

  return (
    <AuthContext.Provider value={{ token, email, name, avatarUrl, login, logout, isLoggingIn }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);


